import type { Indicator, IndicatorContext } from '@idpe/schema';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '~/components/ui/Badge.js';
import { Button } from '~/components/ui/Button.js';
import { ConfirmDialog } from '~/components/ui/Modal.js';
import { Spinner } from '~/components/ui/Spinner.js';
import { IndicatorFormDialog } from '~/features/indicators/IndicatorFormDialog.js';
import { useDeleteIndicator, useIndicatorsList } from '~/features/indicators/hooks.js';
import { ApiError } from '~/lib/api.js';

export const Route = createFileRoute('/_auth/indicators')({
  component: IndicatorsPage,
});

const CONTEXT_BADGE: Record<
  IndicatorContext,
  { label: string; tone: 'primary' | 'success' | 'neutral' }
> = {
  home: { label: 'Home', tone: 'primary' },
  numeros: { label: 'IDPE em Números', tone: 'success' },
  both: { label: 'Home + Números', tone: 'neutral' },
};

function IndicatorsPage() {
  const { data, isLoading, error, refetch } = useIndicatorsList();
  const del = useDeleteIndicator();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Indicator | undefined>(undefined);
  const [deleting, setDeleting] = useState<Indicator | undefined>(undefined);

  async function handleDelete() {
    if (!deleting) return;
    try {
      await del.mutateAsync(deleting.id);
      toast.success('Indicador removido.');
      setDeleting(undefined);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.displayMessage : 'Erro ao remover.');
    }
  }

  return (
    <div className="page">
      <header className="page-head">
        <p className="eyebrow">/ O site</p>
        <h1 className="page-title">Números do instituto</h1>
        <p className="page-sub">
          Os números grandes que aparecem na home e na página "IDPE em Números". Cada número escolhe
          em qual das duas aparece. Escreva o valor como quer que seja lido — "6 bilhões+", "9 + DF"
          —, sem se preocupar com formatação.
        </p>
      </header>

      <section>
        <div className="toolbar">
          <div className="toolbar__row">
            <p style={{ color: 'var(--fg-muted)' }}>
              {data ? `${data.length} ${data.length === 1 ? 'indicador' : 'indicadores'}` : '—'}
            </p>
            <Button variant="primary" onClick={() => setCreating(true)}>
              Novo indicador
            </Button>
          </div>
        </div>

        {isLoading && (
          <div className="dt__state">
            <Spinner size="lg" />
          </div>
        )}

        {error && (
          <div className="dt__state">
            <p className="dt__state-title">Não foi possível carregar.</p>
            <p className="dt__state-sub">{error.displayMessage}</p>
            <Button variant="secondary" onClick={() => void refetch()}>
              Tentar novamente
            </Button>
          </div>
        )}

        {data && data.length === 0 && (
          <div className="dt__state">
            <p className="dt__state-title">Nenhum indicador ainda.</p>
            <Button variant="primary" onClick={() => setCreating(true)}>
              Criar primeiro indicador
            </Button>
          </div>
        )}

        {data && data.length > 0 && (
          <div className="indicator-grid">
            {data.map((ind) => (
              <article key={ind.id} className="indicator-card">
                <header className="indicator-card__head">
                  <Badge tone={CONTEXT_BADGE[ind.contexto].tone}>
                    {CONTEXT_BADGE[ind.contexto].label}
                  </Badge>
                  {!ind.visivel && <Badge tone="muted">oculto</Badge>}
                </header>
                <p className="indicator-card__value">
                  {ind.prefixo && <span className="indicator-card__affix">{ind.prefixo} </span>}
                  {ind.valor}
                  {ind.sufixo && <span className="indicator-card__affix"> {ind.sufixo}</span>}
                </p>
                <p className="indicator-card__desc">{ind.descricao}</p>
                <p className="indicator-card__meta">
                  <code>{ind.chave}</code> · ordem {ind.ordem}
                </p>
                <footer className="indicator-card__actions">
                  <Button variant="secondary" size="sm" onClick={() => setEditing(ind)}>
                    Editar
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleting(ind)}>
                    Excluir
                  </Button>
                </footer>
              </article>
            ))}
          </div>
        )}
      </section>

      <IndicatorFormDialog open={creating} onClose={() => setCreating(false)} />
      <IndicatorFormDialog
        open={!!editing}
        indicator={editing}
        onClose={() => setEditing(undefined)}
      />
      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(undefined)}
        onConfirm={handleDelete}
        title="Excluir indicador?"
        description={`O indicador "${deleting?.chave}" será removido permanentemente. Para apenas escondê-lo no site sem perder o histórico, edite e desmarque "Visível".`}
        confirmLabel="Excluir"
        variant="danger"
        loading={del.isPending}
      />
    </div>
  );
}
